import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { getWorkspaceById } from './workspaces'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'

const logger = createIsomorphicLogger('db.presets')

export const getPresetById = async (supabase: SupabaseClient<Database>, presetId: string) => {
  const { data: preset, error } = await supabase
    .from("presets")
    .select("*")
    .eq("id", presetId)
    .single()

  if (!preset) {
		logError(logger, error, 'Failed to fetch Preset', { presetId })
    throw new Error(error.message)
  }

  return preset
}

export const getOptionalPresetById = async (supabase: SupabaseClient<Database>, presetId: string) => {
  const { data: preset, error } = await supabase
    .from("presets")
    .select("*")
    .eq("id", presetId)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Preset', { presetId })
    throw new Error(error.message)
  }

  return preset
}

export const getOptionalPresetByName = async (supabase: SupabaseClient<Database>, presetName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      presets (*)
    `
    )
    .eq("id", workspaceId)
    .eq("presets.name", presetName)
    .maybeSingle()

	if (error) {
		logError(logger, error, 'Failed to fetch optional Preset by name', { presetName, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.presets[0] : null
}

export const getPresetWorkspacesByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      presets (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
		logError(logger, error, 'Failed to fetch Preset workspaces', { workspaceId })
    throw new Error(error.message)
  }

  return workspace.presets ?? []
}

export const getPresetsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'presets'>[]> => {
	const wsPresets = await getPresetWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'presets'>[] = []

	for (const [i, preset] of wsPresets.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "presets", context: "ANY", uuid: preset.id, tenant:preset.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "presets", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...preset, permissions: perms })
	}

	return result
}

export const getPresetWorkspacesByPresetId = async (supabase: SupabaseClient<Database>, presetId: string) => {
  const { data: preset, error } = await supabase
    .from("presets")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", presetId)
    .single()

  if (!preset) {
		logError(logger, error, 'Failed to fetch Preset workspaces', { presetId })
    throw new Error(error.message)
  }

  return preset
}

export const getAutoSharePresets = async (supabase: SupabaseClient<Database>) => {
  const { data: presets, error } = await supabase
    .from("presets")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-shared Presets')
    throw new Error(error.message)
  }

  return presets
}

export const createPreset = async (
	supabase: SupabaseClient<Database>,
  preset: TablesInsert<"presets">,
  workspace: Tables<"workspaces">
) => {
	const presetWithTenant = {
		...preset,
		owning_tenant: workspace.tenant_id
	}
  const { data: createdPreset, error } = await supabase
    .from("presets")
    .insert([presetWithTenant])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create Preset', { preset: printObject(preset) })
    throw new Error(error.message)
  }

  await createPresetWorkspace(supabase, {
    user_id: preset.user_id,
    preset_id: createdPreset.id,
    workspace_id: workspace.id
  })

	logger.info('Preset created successfully', { presetId: createdPreset.id })
  return createdPreset
}

export const createPresets = async (
	supabase: SupabaseClient<Database>,
  presets: TablesInsert<"presets">[],
  workspace: Tables<"workspaces">
) => {
	const presetsWithTenant = presets.map(preset => ({
		...preset,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdPresets, error } = await supabase
    .from("presets")
    .insert(presetsWithTenant)
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create Presets', { presets: printObject(presets) })
    throw new Error(error.message)
  }

  await createPresetWorkspaces(
		supabase,
    createdPresets.map(preset => ({
      user_id: preset.user_id,
      preset_id: preset.id,
	    workspace_id: workspace.id
    }))
  )

	logger.info('Presets created successfully', { presets: printObject(presets) })
  return createdPresets
}

export const createPresetWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  preset_id: string
  workspace_id: string
}) => {
  const { data: createdPresetWorkspace, error } = await supabase
    .from("preset_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create Preset workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

	logger.info('Preset workspace created successfully', { presetWorkspace: printObject(createdPresetWorkspace) })
  return createdPresetWorkspace
}

export const createPresetWorkspaces = async (supabase: SupabaseClient<Database>,
  items: { user_id: string; preset_id: string; workspace_id: string }[]
) => {
  const { data: createdPresetWorkspaces, error } = await supabase
    .from("preset_workspaces")
    .upsert(items, { onConflict: 'preset_id, workspace_id' })
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create Preset workspaces', { items: printObject(items) })
		throw new Error(error.message)
	}

	logger.info('Preset workspaces created successfully',  { presetWorkspaces: printObject(createdPresetWorkspaces) })
  return createdPresetWorkspaces
}

export const updatePreset = async (
	supabase: SupabaseClient<Database>,
  presetId: string,
  preset: TablesUpdate<"presets">
) => {
  const { data: updatedPreset, error } = await supabase
    .from("presets")
    .update(preset)
    .eq("id", presetId)
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to update Preset', { preset: printObject(preset) })
    throw new Error(error.message)
  }

  return updatedPreset
}

export const deletePreset = async (supabase: SupabaseClient<Database>, presetId: string) => {
  const { error } = await supabase.from("presets").delete().eq("id", presetId)

  if (error) {
		logError(logger, error, 'Failed to delete Preset', { presetId })
    throw new Error(error.message)
  }

  return true
}

export const deletePresetWorkspace = async (
	supabase: SupabaseClient<Database>,
  presetId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("preset_workspaces")
    .delete()
    .eq("preset_id", presetId)
    .eq("workspace_id", workspaceId)

  if (error) {
		logError(logger, error, 'Failed to delete Preset workspace', { presetId, workspaceId })
		throw new Error(error.message)
	}

  return true
}
